import { formatLocale } from 'd3-format';
import { timeMonth, timeYear } from 'd3-time';
import { timeFormatLocale } from 'd3-time-format';
import { isNil } from 'lodash-es';
import type { Formatter, LocaleDefinition } from './types';

// TODO: Consider if there is a better fallback value to use.
export const defaultFallbackValue = '—';

/**
 * Creates an object that can be used to format values and times based on
 * the definitions passed into it when constructed.
 */
export function createFormatter(
  { timeLocale: timeLocaleDefinition, formatLocale: formatLocaleDefinition }: LocaleDefinition,
  defaultTimeZone: string,
): Formatter {
  const formatLocaleInstance = formatLocale(formatLocaleDefinition);
  const timeLocale = timeFormatLocale(timeLocaleDefinition);

  // Integer formatters

  const integerFormat = formatLocaleInstance.format(',');

  function formatInteger(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return integerFormat(Math.round(value));
  }

  const headlineIntegerFormat = formatLocaleInstance.format(',.3~s');

  function formatHeadlineNumber(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return headlineIntegerFormat(Math.round(value));
  }

  // Inventory formatters

  const inventoryFormat = formatLocaleInstance.format(',.2f');

  function formatInventory(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return inventoryFormat(value);
  }

  // Money formatters

  // Currently assumes 2 digits for currency minor units:
  const moneyFormat = formatLocaleInstance.format('$,.2f');
  const noMinorUnitMoneyFormat = formatLocaleInstance.format('$,.0f');

  function formatMoney(value: number | null, includeMinorUnit = false) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return (includeMinorUnit ? moneyFormat : noMinorUnitMoneyFormat)(value);
  }

  const headlineMoneyFormat = formatLocaleInstance.format('$,.3~s');

  function formatHeadlineMoney(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return headlineMoneyFormat(Math.round(value));
  }

  const headlineMoneyDeltaFormat = formatLocaleInstance.format('+$,.3~s');

  function formatHeadlineMoneyDelta(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return headlineMoneyDeltaFormat(Math.round(value));
  }

  function formatPriceBand(value: { minimum: number; maximum: number | 'infinity' } | null) {
    if (isNil(value) || isNil(value?.minimum) || isNil(value?.maximum)) {
      return defaultFallbackValue;
    }
    if (typeof value.minimum === 'number' && typeof value.maximum === 'number' && value.minimum > value.maximum) {
      throw new Error(`Invalid price range. Got: ${JSON.stringify(value)}`);
    }
    if (value.minimum === 0 && value.maximum === 'infinity') {
      return 'All prices';
    }
    if (value.maximum === 'infinity') {
      return `${formatMoney(value.minimum)} or more`;
    }
    if (value.minimum === 0) {
      return `${formatMoney(value.maximum)} or less`;
    }
    return `${formatMoney(value.minimum)} to ${formatMoney(value.maximum)}`;
  }

  const currencySymbol = formatLocaleDefinition.currency[0];

  // Percentage formatters

  function formatPercentage(value: number | null, numberOfDecimalPlaces = 0) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return formatLocaleInstance.format(`.${numberOfDecimalPlaces}%`)(value);
  }

  const percentageDeltaFormatter = formatLocaleInstance.format('+.0%');

  function formatPercentageDelta(value: number | null) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return percentageDeltaFormatter(value);
  }

  const percentageAbsoluteDeltaFormatter = formatLocaleInstance.format('+.0%');

  function formatPercentageAbsoluteChange(value: number | null | undefined) {
    if (isNil(value)) {
      return '';
    }
    const percent = percentageAbsoluteDeltaFormatter(value).replace('%', '');
    // TODO internationalize `pts`? 🤔
    const percentZeroChangeUnsigned = percent.slice(1) === '0' ? 'No change' : `${percent} pts`;
    return percentZeroChangeUnsigned;
  }

  function formatPointsDelta(value: number | null | undefined): string {
    if (isNil(value)) {
      return '';
    }

    const normalisedPoints = value * 100;
    // Ensure negative numbers are rounded up and positives are rounded down
    const points = value < 0 ? Math.ceil(normalisedPoints) : Math.floor(normalisedPoints);
    const sign = points > 0 ? '+' : '';

    if (points === 0) {
      return 'No difference';
    }

    return `${sign}${points} pt`;
  }

  function formatPercentageToScore(value: number | null | undefined) {
    if (isNil(value)) {
      return defaultFallbackValue;
    }
    return Math.floor(value * 100).toString();
  }

  // Time formatters

  const zonedDateFormat = timeLocale.format('%B %d, %Y');

  function formatZonedDate(zonedDate: Date | null) {
    if (isNil(zonedDate)) {
      return defaultFallbackValue;
    }
    return zonedDateFormat(zonedDate);
  }

  const zonedDateWithTimeFormat = timeLocale.format('%B %d, %Y %I:%M %p');

  function formatZonedDateWithTime(zonedDate: Date | null) {
    if (!zonedDate) {
      return defaultFallbackValue;
    }
    return zonedDateWithTimeFormat(zonedDate);
  }

  const zonedDateAsTimestampFormat = timeLocale.format('%x at %H:%M');

  function formatZonedDateAsTimestamp(zonedDate: Date | null) {
    if (!zonedDate) {
      return defaultFallbackValue;
    }
    return zonedDateAsTimestampFormat(zonedDate);
  }

  const zonedDateSortableFormat = timeLocale.format('%Y%m%d');

  function formatZonedDateSortable(zonedDate: Date | null) {
    if (isNil(zonedDate)) {
      return defaultFallbackValue;
    }
    return zonedDateSortableFormat(zonedDate);
  }

  const localDateFormat = timeLocale.format('%B %d, %Y');

  function formatLocalDate(localDate: Date | null) {
    if (!localDate) {
      return defaultFallbackValue;
    }
    return localDateFormat(localDate);
  }

  const localDateWithWeekdayFormat = timeLocale.format('%a %B %d, %Y');

  function formatLocalDateWithWeekdayName(localDate: Date | null) {
    if (isNil(localDate)) {
      return defaultFallbackValue;
    }
    return localDateWithWeekdayFormat(localDate);
  }

  const localDateAsTimestampFormat = timeLocale.format('%x at %H:%M');

  function formatLocalDateAsTimestamp(localDate: Date | null) {
    if (!localDate) {
      return defaultFallbackValue;
    }
    return localDateAsTimestampFormat(localDate);
  }

  function formatMonthName(monthNumber: number | null) {
    if (isNil(monthNumber)) {
      return defaultFallbackValue;
    }
    return timeLocaleDefinition.months[monthNumber];
  }

  function formatMonthNameShort(monthNumber: number | null) {
    if (isNil(monthNumber)) {
      return defaultFallbackValue;
    }
    return timeLocaleDefinition.shortMonths[monthNumber];
  }

  function formatWeekdayShort(weekday: number | null) {
    if (isNil(weekday)) {
      return defaultFallbackValue;
    }
    return timeLocaleDefinition.shortDays[weekday];
  }

  function formatWeekdayExtraShort(weekday: number | null) {
    if (isNil(weekday)) {
      return defaultFallbackValue;
    }
    return timeLocaleDefinition.extraShortDays[weekday];
  }

  const formatMultiFormatWeek = timeLocale.format('%b %d');
  const formatMultiFormatMonth = timeLocale.format('%b');
  const formatMultiFormatYear = timeLocale.format('%Y');

  function multiFormatZonedDate(date: Date | null) {
    if (isNil(date)) {
      return defaultFallbackValue;
    }

    let formatter = formatMultiFormatYear;

    if (timeMonth(date) < date) {
      formatter = formatMultiFormatWeek;
    } else if (timeYear(date) < date) {
      formatter = formatMultiFormatMonth;
    }

    return formatter(date);
  }

  function formatDateAsShortString(zonedDate: Date | null) {
    const zonedDateCustomFormat = timeLocale.format('%d/%m/%Y');

    if (isNil(zonedDate)) {
      return defaultFallbackValue;
    }
    return zonedDateCustomFormat(zonedDate);
  }

  function formatEffort(effort: number | null) {
    if (isNil(effort)) {
      return defaultFallbackValue;
    }
    switch (effort) {
      case 1:
        return 'S';
      case 2:
        return 'M';
      case 3:
        return 'L';
      case 4:
        return 'XL';
      case 5:
        return 'XXL';
      default:
        return 'Invalid effort';
    }
  }

  /**
   * Returns a string with the number of days since a given ISO datetime.
   */
  const formatDaysSince = (isoDateStr: Date) => {
    const relativeTimeFormat = new Intl.RelativeTimeFormat('en', { style: 'short' });
    const givenDate = isoDateStr ? new Date(isoDateStr) : new Date();
    const currentDate = new Date();
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const differenceInMilliseconds = currentDate.valueOf() - givenDate.valueOf();
    const differenceInDays = Math.floor(differenceInMilliseconds / millisecondsPerDay);
    const difference = relativeTimeFormat.format(differenceInDays * -1, 'day');
    return difference;
  };

  return {
    formatInteger,
    formatHeadlineNumber,

    formatInventory,

    formatMoney,
    formatHeadlineMoney,
    formatHeadlineMoneyDelta,
    formatPriceBand,
    currencySymbol,

    formatPercentage,
    formatPercentageDelta,
    formatPercentageAbsoluteChange,
    formatPointsDelta,
    formatPercentageToScore,

    formatZonedDate,
    formatZonedDateWithTime,
    formatZonedDateAsTimestamp,
    formatZonedDateSortable,
    multiFormatZonedDate,
    formatDateAsShortString,

    formatLocalDate,
    formatLocalDateWithWeekdayName,
    formatLocalDateAsTimestamp,

    formatMonthName,
    formatMonthNameShort,
    formatWeekdayShort,
    formatWeekdayExtraShort,

    formatEffort,

    formatDaysSince,

    weekStart: timeLocaleDefinition.weekStart,
    defaultTimeZone,
  };
}
